import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  CreateUseCaseRequest,
  FindUseCasesFilters,
  UpdateUseCaseRequest,
  UseCase,
  UseCaseCandidatesFilters,
  UseCaseCandidatesResponse,
  UseCasesResponse,
} from '@guilleumes/types'

import { api } from '@/services/api'

/**
 * Casos d'ús — `plans/reservation-context-and-use-cases.md` §F3.
 *
 * Clau jeràrquica `['use-cases', …]` perquè invalidar l'arrel després d'escriure
 * refresqui alhora la llista de `/casos`, la secció de la fitxa i el comptador de
 * candidats (un cas nou canvia `useCaseCount` d'una reserva).
 */

/** Llista de casos amb filtres. La secció de la fitxa passa `reservationId`. */
export function useUseCases(filters: FindUseCasesFilters = {}, enabled = true) {
  return useQuery<UseCasesResponse>({
    queryKey: ['use-cases', filters],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (filters.reservationId) params.reservationId = filters.reservationId
      if (filters.beds24Id) params.beds24Id = filters.beds24Id
      if (filters.topic) params.topic = filters.topic
      if (filters.dataKey) params.dataKey = filters.dataKey
      if (filters.questionRef) params.questionRef = filters.questionRef
      if (filters.status) params.status = filters.status
      if (filters.untagged) params.untagged = 'true'
      if (filters.q) params.q = filters.q
      if (filters.limit !== undefined) params.limit = String(filters.limit)
      if (filters.offset !== undefined) params.offset = String(filters.offset)

      const response = await api.get('/use-cases', { params })
      return response.data
    },
    enabled,
    // Els escriuen dues persones a mà: no cal rellegir-ho cada pocs segons.
    staleTime: 60 * 1000,
  })
}

/**
 * Reserves per repassar (pestanya "Candidats").
 *
 * `staleTime` més llarg que la llista: el que hi surt són reserves passades i el que
 * canvia és el que hi escrivim a sobre, no elles.
 */
export function useUseCaseCandidates(filters: UseCaseCandidatesFilters = {}, enabled = true) {
  return useQuery<UseCaseCandidatesResponse>({
    queryKey: ['use-cases', 'candidates', filters],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (filters.checkInFrom) params.checkInFrom = filters.checkInFrom
      if (filters.checkInTo) params.checkInTo = filters.checkInTo
      if (filters.phoneState) params.phoneState = filters.phoneState
      if (filters.hasWhatsapp) params.hasWhatsapp = filters.hasWhatsapp
      if (filters.channel) params.channel = filters.channel
      if (filters.groupsOnly) params.groupsOnly = 'true'
      if (filters.hasClient !== undefined) params.hasClient = String(filters.hasClient)
      if (filters.hasUseCases !== undefined) params.hasUseCases = String(filters.hasUseCases)
      if (filters.limit !== undefined) params.limit = String(filters.limit)
      if (filters.offset !== undefined) params.offset = String(filters.offset)

      const response = await api.get('/use-cases/candidates', { params })
      return response.data
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

/** Escriu un cas nou. */
export function useCreateUseCase() {
  const queryClient = useQueryClient()

  return useMutation<UseCase, Error, CreateUseCaseRequest>({
    mutationFn: async (body) => {
      const response = await api.post('/use-cases', body)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['use-cases'] })
    },
  })
}

/**
 * Edita un cas: classificació, textos, reserves lligades o estat.
 *
 * El servidor no deixa recular l'estat ni tancar sense resolució; aquí no s'hi
 * duplica la regla, només es propaga l'error al toast.
 */
export function useUpdateUseCase() {
  const queryClient = useQueryClient()

  return useMutation<UseCase, Error, { id: string } & UpdateUseCaseRequest>({
    mutationFn: async ({ id, ...body }) => {
      const response = await api.patch(`/use-cases/${id}`, body)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['use-cases'] })
    },
  })
}

/**
 * Treu un cas de la vista. **No** és "ja no cal" —això és `status: 'done'`, que el
 * conserva—: és per a un cas escrit per error. Només ADMIN.
 */
export function useDeleteUseCase() {
  const queryClient = useQueryClient()

  return useMutation<{ id: string; deletedAt: string }, Error, string>({
    mutationFn: async (id) => {
      const response = await api.delete(`/use-cases/${id}`)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['use-cases'] })
    },
  })
}
